import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CasinoIcon from '@mui/icons-material/Casino';
import ReplayIcon from '@mui/icons-material/Replay';
import GroupsIcon from '@mui/icons-material/Groups';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import { motion, AnimatePresence } from 'framer-motion';
import { toPersianDigits } from '../common/JalaliDatePicker';
import { RaffleItem, RaffleWinner } from '../../services/raffle.service';
import '../../assets/fonts/lalezar/lalezar.css';

/**
 * صحنهٔ نمایشی قرعه‌کشی — تمام‌صفحه، Glassmorphism تیره، مناسب ضبط برای لایو.
 * برنده‌ها از قبل توسط سرور قطعی شده‌اند؛ اینجا فقط نمایش است.
 * ترتیب اعلام: سایر برنده‌ها → نفر سوم → نفر دوم → نفر اول.
 * روی نتایج، هر رتبه قابل «قرعه‌کشی مجدد» است (رد برنده و انتخاب جایگزین از همان seed).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  raffle: RaffleItem;
  candidatesSample: string[];
  /** رد برندهٔ فعلی رتبه و گرفتن جایگزین از سرور؛ در صورت نبود، دکمهٔ redraw مخفی است */
  onRedrawRank?: (rank: number) => Promise<RaffleWinner>;
  onError?: (message: string) => void;
}

type Phase = 'intro' | 'spinning' | 'revealed' | 'results' | 'redraw-wait' | 'redraw-revealed';

const GOLD = '#F5B301';
const SILVER = '#C3CDD9';
const BRONZE = '#D08A45';
const RANK_COLORS: Record<number, string> = { 1: GOLD, 2: SILVER, 3: BRONZE };
const BG = '#0B1020';
/** فونت نمایشی صحنه — تیترها و اسم برنده‌ها */
const DISPLAY_FONT = 'Lalezar, Vazirmatn, sans-serif';

const rankLabel = (rank: number): string => {
  if (rank === 1) return 'نفر اول';
  if (rank === 2) return 'نفر دوم';
  if (rank === 3) return 'نفر سوم';
  return `برندهٔ رتبهٔ ${toPersianDigits(String(rank))}`;
};

const formatAmount = (amount?: number): string | null =>
  amount !== undefined && amount !== null ? `${Number(amount).toLocaleString('fa-IR')} تومان` : null;

/** پنل شیشه‌ای پایه — backdrop blur + بوردر ظریف + بازتاب نور */
const glass = (extra: object = {}) => ({
  background: 'linear-gradient(160deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 4,
  boxShadow: '0 24px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
  ...extra,
});

// ---------------------------------------------------------------------------
// کانفتی — canvas دست‌ساز (بدون وابستگی جدید)
// ---------------------------------------------------------------------------

interface ConfettiHandle {
  burst: (big?: boolean) => void;
}

const ConfettiCanvas = React.forwardRef<ConfettiHandle>((_props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<any[]>([]);
  const rafRef = useRef<number | null>(null);

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const alive: any[] = [];
    for (const p of particlesRef.current) {
      p.vy += 0.12;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= 1;
      if (p.life > 0 && p.y < canvas.height + 30) {
        alive.push(p);
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.min(1, p.life / 40);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.66);
        ctx.restore();
      }
    }
    particlesRef.current = alive;
    if (alive.length) {
      rafRef.current = requestAnimationFrame(loop);
    } else {
      rafRef.current = null;
    }
  }, []);

  React.useImperativeHandle(ref, () => ({
    burst: (big = false) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const colors = [GOLD, '#FF5E7E', '#4DD0E1', '#9CCC65', '#BA68C8', '#FFB74D', '#FFFFFF'];
      const count = big ? 260 : 120;
      const cx = canvas.width / 2;
      const cy = canvas.height * 0.35;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (big ? 9 : 6) * (0.4 + Math.random());
        particlesRef.current.push({
          x: cx + (Math.random() - 0.5) * 140,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - (big ? 5 : 3),
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          size: 6 + Math.random() * 8,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 140 + Math.random() * 80,
        });
      }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
    },
  }));

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 30 }}
    />
  );
});
ConfettiCanvas.displayName = 'ConfettiCanvas';

// ---------------------------------------------------------------------------
// صحنهٔ اصلی
// ---------------------------------------------------------------------------

const RaffleDrawStage: React.FC<Props> = ({ open, onClose, raffle, candidatesSample, onRedrawRank, onError }) => {
  const [phase, setPhase] = useState<Phase>('intro');
  const [queueIndex, setQueueIndex] = useState(0);
  const [reelName, setReelName] = useState('');
  const [reelBlur, setReelBlur] = useState(0);
  const [redrawTarget, setRedrawTarget] = useState<number | null>(null); // رتبهٔ در حال قرعه‌کشی مجدد
  const [redrawConfirm, setRedrawConfirm] = useState<RaffleWinner | null>(null);
  const [redrawWinner, setRedrawWinner] = useState<RaffleWinner | null>(null);
  const [redrawBusy, setRedrawBusy] = useState(false);
  /** redraw از کجا شروع شد؟ لحظهٔ اعلام (ادامهٔ نمایش) یا صفحهٔ نتایج */
  const [redrawOrigin, setRedrawOrigin] = useState<'reveal' | 'results'>('results');
  const confettiRef = useRef<ConfettiHandle>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockRef = useRef<{ winner: RaffleWinner; started: number } | null>(null);

  // ترتیب اعلام: رتبه‌های بالا آخر — [N..4] بعد ۳، ۲، ۱
  const revealQueue: RaffleWinner[] = useMemo(() => {
    const winners = [...(raffle.winners || [])];
    const others = winners.filter((w) => w.rank > 3).sort((a, b) => b.rank - a.rank);
    const top = winners.filter((w) => w.rank <= 3).sort((a, b) => b.rank - a.rank);
    return [...others, ...top];
  }, [raffle.winners]);

  const current = revealQueue[queueIndex];

  const names = useMemo(() => {
    const pool = candidatesSample.length ? candidatesSample : (raffle.winners || []).map((w) => w.name);
    return pool.length ? pool : ['—'];
  }, [candidatesSample, raffle.winners]);

  useEffect(() => {
    if (open) {
      setPhase(raffle.status === 'archived' ? 'results' : 'intro');
      setQueueIndex(0);
      setReelName('');
      setRedrawTarget(null);
      setRedrawConfirm(null);
      setRedrawWinner(null);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** چرخش اسلات‌ماشینی: تند شروع می‌شود، با easing کند می‌شود و روی برنده قفل می‌کند */
  const spinFor = useCallback(
    (winner: RaffleWinner, durationMs: number, donePhase: Phase) => {
      const start = performance.now();
      const tick = () => {
        const t = Math.min(1, (performance.now() - start) / durationMs);
        if (t >= 1) {
          setReelName(winner.name);
          setReelBlur(0);
          setPhase(donePhase);
          confettiRef.current?.burst(winner.rank === 1);
          return;
        }
        setReelName(names[Math.floor(Math.random() * names.length)]);
        setReelBlur(t < 0.55 ? 4 : t < 0.8 ? 2 : 0.5);
        const interval = 35 + 320 * t * t * t;
        timerRef.current = setTimeout(tick, interval);
      };
      tick();
    },
    [names],
  );

  const startNext = useCallback(() => {
    const winner = revealQueue[queueIndex];
    if (!winner) {
      setPhase('results');
      return;
    }
    setPhase('spinning');
    const duration = winner.rank === 1 ? 5200 : winner.rank <= 3 ? 3800 : 1600;
    spinFor(winner, duration, 'revealed');
  }, [queueIndex, revealQueue, spinFor]);

  const advance = useCallback(() => {
    if (queueIndex + 1 >= revealQueue.length) {
      setPhase('results');
      confettiRef.current?.burst(true);
      timerRef.current = setTimeout(() => confettiRef.current?.burst(true), 900);
    } else {
      setQueueIndex((i) => i + 1);
      setPhase('spinning');
    }
  }, [queueIndex, revealQueue.length]);

  useEffect(() => {
    if (open && phase === 'spinning' && queueIndex > 0 && !redrawTarget) {
      startNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIndex]);

  const skipToResults = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase('results');
  };

  // ------------------------------------------------------------- redraw flow

  const beginRedraw = async (winner: RaffleWinner) => {
    if (!onRedrawRank) return;
    const origin: 'reveal' | 'results' = phase === 'results' ? 'results' : 'reveal';
    setRedrawOrigin(origin);
    setRedrawConfirm(null);
    setRedrawTarget(winner.rank);
    setRedrawBusy(true);
    setPhase('redraw-wait');
    // چرخش بی‌هدف تا رسیدن پاسخ سرور
    const freeSpin = () => {
      setReelName(names[Math.floor(Math.random() * names.length)]);
      setReelBlur(4);
      timerRef.current = setTimeout(freeSpin, 45);
    };
    freeSpin();
    try {
      const newWinner = await onRedrawRank(winner.rank);
      if (timerRef.current) clearTimeout(timerRef.current);
      setRedrawWinner(newWinner);
      lockRef.current = { winner: newWinner, started: performance.now() };
      spinFor(newWinner, 3000, 'redraw-revealed');
      setRedrawBusy(false);
    } catch (e: any) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setRedrawBusy(false);
      setRedrawTarget(null);
      setReelBlur(0);
      setPhase(origin === 'results' ? 'results' : 'revealed');
      onError?.(e?.response?.data?.message || e?.message || 'خطا در قرعه‌کشی مجدد');
    }
  };

  const topThree = (raffle.winners || []).filter((w) => w.rank <= 3).sort((a, b) => a.rank - b.rank);
  const others = (raffle.winners || []).filter((w) => w.rank > 3).sort((a, b) => a.rank - b.rank);
  const podiumOrder = [
    topThree.find((w) => w.rank === 2),
    topThree.find((w) => w.rank === 1),
    topThree.find((w) => w.rank === 3),
  ];
  const podiumHeights = [120, 170, 96];

  const isSpinningPhase = phase === 'spinning' || phase === 'revealed' || phase === 'redraw-wait' || phase === 'redraw-revealed';
  const reelWinner = redrawTarget ? redrawWinner : current;
  const reelRank = redrawTarget ?? current?.rank ?? 0;
  const reelColor = RANK_COLORS[reelRank] || GOLD;
  const revealed = phase === 'revealed' || phase === 'redraw-revealed';

  // ------------------------------------------------------------------ render

  return (
    <Dialog open={open} fullScreen onClose={onClose} PaperProps={{ sx: { bgcolor: BG } }}>
      <Box
        dir="rtl"
        sx={{
          position: 'relative',
          minHeight: '100%',
          background: BG,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* نورهای محیطی پس‌زمینه */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `
              radial-gradient(900px 500px at 70% -10%, rgba(99,102,241,0.22) 0%, transparent 60%),
              radial-gradient(700px 500px at 15% 110%, rgba(245,179,1,0.14) 0%, transparent 55%),
              radial-gradient(1400px 800px at 50% 40%, rgba(30,41,59,0.5) 0%, transparent 100%)
            `,
          }}
        />
        <motion.div
          animate={{ opacity: [0.5, 0.9, 0.5] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: '-120px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 620,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(245,179,1,0.14), transparent)',
            pointerEvents: 'none',
          }}
        />
        {/* شفق‌های آهستهٔ پس‌زمینه */}
        <motion.div
          animate={{ x: [0, 60, -40, 0], y: [0, -30, 20, 0], opacity: [0.35, 0.6, 0.4, 0.35] }}
          transition={{ repeat: Infinity, duration: 18, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            bottom: '-160px',
            right: '-120px',
            width: 560,
            height: 460,
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(77,208,225,0.12), transparent)',
            pointerEvents: 'none',
          }}
        />
        <motion.div
          animate={{ x: [0, -50, 30, 0], y: [0, 25, -20, 0], opacity: [0.3, 0.55, 0.35, 0.3] }}
          transition={{ repeat: Infinity, duration: 22, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '-140px',
            width: 520,
            height: 520,
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(186,104,200,0.10), transparent)',
            pointerEvents: 'none',
          }}
        />

        <ConfettiCanvas ref={confettiRef} />

        {/* هدر */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2.5, zIndex: 40 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={glass({
                width: 42,
                height: 42,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
            >
              <CasinoIcon sx={{ color: GOLD, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ lineHeight: 1.3, fontFamily: DISPLAY_FONT, fontSize: 19 }}>
                {raffle.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                قرعه‌کشی رسمی کازماشاپ
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {(phase === 'spinning' || phase === 'revealed') && (
              <Button
                size="small"
                color="inherit"
                startIcon={<SkipNextIcon />}
                onClick={skipToResults}
                sx={{ opacity: 0.55, '&:hover': { opacity: 1 } }}
              >
                رد کردن نمایش
              </Button>
            )}
            <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        {/* بدنه */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, pb: 4, zIndex: 20 }}>
          {/* ------------------------------------------------ شروع */}
          {phase === 'intro' && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Box sx={glass({ px: { xs: 3, sm: 8 }, py: 6, textAlign: 'center', maxWidth: 640 })}>
                <Stack spacing={3} alignItems="center">
                  <motion.div
                    animate={{ rotate: [0, -8, 8, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
                  >
                    <CasinoIcon sx={{ fontSize: 88, color: GOLD, filter: `drop-shadow(0 0 28px ${GOLD}66)` }} />
                  </motion.div>
                  <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 34, sm: 44 }, lineHeight: 1.2 }}>
                    {raffle.status === 'draft' ? 'قرعه‌کشی آماده است' : 'نمایش قرعه‌کشی'}
                  </Typography>
                  <Stack direction="row" spacing={1.5}>
                    {[
                      // تعداد شرکت‌کننده فقط وقتی تیکش روشن باشد نمایش داده می‌شود
                      ...(raffle.options?.showParticipants === true
                        ? [
                            {
                              icon: <GroupsIcon sx={{ fontSize: 18, color: '#4DD0E1' }} />,
                              label: 'شرکت‌کننده',
                              value: toPersianDigits(String(raffle.draw?.participants ?? 0)),
                            },
                          ]
                        : []),
                      {
                        icon: <CardGiftcardIcon sx={{ fontSize: 18, color: '#FF5E7E' }} />,
                        label: 'جایزه',
                        value: toPersianDigits(String((raffle.prizes || []).length)),
                      },
                      // مجموع ارزش جوایز اگر مبلغ ثبت شده باشد، وگرنه تعداد برنده
                      ...((raffle.prizes || []).some((p) => p.amount)
                        ? [
                            {
                              icon: <EmojiEventsIcon sx={{ fontSize: 18, color: GOLD }} />,
                              label: 'مجموع جوایز (تومان)',
                              value: (raffle.prizes || [])
                                .reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
                                .toLocaleString('fa-IR'),
                            },
                          ]
                        : [
                            {
                              icon: <EmojiEventsIcon sx={{ fontSize: 18, color: GOLD }} />,
                              label: 'برنده',
                              value: toPersianDigits(String(raffle.winnersCount)),
                            },
                          ]),
                    ].map((s) => (
                      <Box key={s.label} sx={glass({ borderRadius: 3, px: 2.5, py: 1.25, minWidth: 96 })}>
                        <Stack alignItems="center" spacing={0.25}>
                          {s.icon}
                          <Typography sx={{ fontSize: 24, fontFamily: DISPLAY_FONT, lineHeight: 1.1 }}>
                            {s.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            {s.label}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    size="large"
                    onClick={startNext}
                    sx={{
                      px: 7,
                      py: 1.5,
                      fontSize: 18,
                      fontWeight: 900,
                      borderRadius: 99,
                      background: `linear-gradient(135deg, ${GOLD}, #FF8F00)`,
                      color: '#1A1200',
                      boxShadow: `0 0 45px ${GOLD}55, inset 0 1px 0 rgba(255,255,255,0.4)`,
                      '&:hover': { background: 'linear-gradient(135deg, #FFC233, #FF9D1C)' },
                    }}
                  >
                    شروع نمایش قرعه‌کشی
                  </Button>
                  <Button size="small" color="inherit" onClick={skipToResults} sx={{ opacity: 0.5 }}>
                    مشاهدهٔ مستقیم نتایج
                  </Button>
                </Stack>
              </Box>
            </motion.div>
          )}

          {/* ------------------------------------------------ چرخش / اعلام (اولیه و مجدد) */}
          {isSpinningPhase && (
            <Stack spacing={4} alignItems="center" sx={{ width: '100%', maxWidth: 760 }}>
              <motion.div
                key={`label-${redrawTarget ?? queueIndex}`}
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {redrawTarget && <ReplayIcon sx={{ fontSize: 30, color: '#4DD0E1' }} />}
                  {reelRank > 0 && reelRank <= 3 && (
                    <EmojiEventsIcon sx={{ fontSize: 40, color: RANK_COLORS[reelRank] }} />
                  )}
                  <Typography
                    sx={{
                      color: RANK_COLORS[reelRank] || '#fff',
                      fontFamily: DISPLAY_FONT,
                      fontSize: { xs: 30, sm: 40 },
                      lineHeight: 1.2,
                      textShadow: reelRank <= 3 ? `0 0 30px ${(RANK_COLORS[reelRank] || GOLD)}55` : 'none',
                    }}
                  >
                    {redrawTarget ? `قرعه‌کشی مجدد — ${rankLabel(redrawTarget)}` : rankLabel(reelRank)}
                  </Typography>
                </Stack>
              </motion.div>

              {/* قاب شیشه‌ای ریل اسم‌ها */}
              <Box
                sx={{
                  width: '100%',
                  borderRadius: 5,
                  p: '2px',
                  background: revealed
                    ? `linear-gradient(135deg, ${reelColor}, transparent 55%, ${reelColor})`
                    : 'linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.04))',
                  boxShadow: revealed ? `0 0 70px ${reelColor}55` : '0 20px 50px rgba(0,0,0,0.55)',
                  transition: 'box-shadow .4s',
                }}
              >
                <Box
                  sx={{
                    borderRadius: 4.6,
                    background: 'linear-gradient(180deg, rgba(17,24,44,0.92), rgba(11,16,32,0.96))',
                    backdropFilter: 'blur(14px)',
                    height: { xs: 130, sm: 165 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* بازتاب نور بالای شیشه */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '8%',
                      right: '8%',
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                      zIndex: 4,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(11,16,32,0.95) 0%, transparent 32%, transparent 68%, rgba(11,16,32,0.95) 100%)',
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  />
                  {revealed && reelWinner ? (
                    <motion.div
                      key={`win-${redrawTarget ?? queueIndex}`}
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                      style={{ zIndex: 3 }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: 40, sm: 64 },
                          fontFamily: DISPLAY_FONT,
                          color: reelColor,
                          textShadow: `0 0 40px ${reelColor}99, 0 2px 0 rgba(0,0,0,0.4)`,
                          lineHeight: 1.15,
                        }}
                      >
                        {reelWinner.name}
                      </Typography>
                    </motion.div>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: 36, sm: 54 },
                        fontFamily: DISPLAY_FONT,
                        color: 'rgba(255,255,255,0.85)',
                        filter: `blur(${reelBlur}px)`,
                        zIndex: 3,
                        lineHeight: 1.15,
                      }}
                    >
                      {reelName || '…'}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* جایزه + دکمهٔ ادامه */}
              <AnimatePresence>
                {revealed && reelWinner && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.35, duration: 0.4 }}
                  >
                    <Stack spacing={2.5} alignItems="center">
                      <Box sx={glass({ borderRadius: 3, px: 4, py: 2, textAlign: 'center' })}>
                        <Stack spacing={0.5} alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CardGiftcardIcon sx={{ fontSize: 20, color: '#FF5E7E' }} />
                            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              {reelWinner.prizeTitle}
                            </Typography>
                          </Stack>
                          {formatAmount(reelWinner.prizeAmount) && (
                            <Typography sx={{ color: GOLD, fontFamily: DISPLAY_FONT, fontSize: 30, lineHeight: 1.2, textShadow: `0 0 24px ${GOLD}55` }}>
                              {formatAmount(reelWinner.prizeAmount)}
                            </Typography>
                          )}
                          {reelWinner.phoneNumber && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.45)', direction: 'ltr', letterSpacing: 1 }}
                            >
                              {toPersianDigits(reelWinner.phoneNumber)}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      {/* تصمیم همین لحظه: تأیید برنده و ادامه، یا قرعه‌کشی مجدد همین جایگاه */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                        {onRedrawRank && raffle.status === 'drawn' && (
                          <Button
                            variant="outlined"
                            size="large"
                            startIcon={<ReplayIcon />}
                            onClick={() => reelWinner && setRedrawConfirm(reelWinner)}
                            sx={{
                              borderColor: 'rgba(77,208,225,0.5)',
                              color: '#4DD0E1',
                              borderRadius: 99,
                              px: 4,
                              backdropFilter: 'blur(8px)',
                              '&:hover': { borderColor: '#4DD0E1', bgcolor: 'rgba(77,208,225,0.1)' },
                            }}
                          >
                            قرعه‌کشی مجدد همین جایگاه
                          </Button>
                        )}
                        <Button
                          size="large"
                          onClick={() => {
                            if (phase === 'redraw-revealed') {
                              setRedrawTarget(null);
                              setRedrawWinner(null);
                              if (redrawOrigin === 'results') {
                                setPhase('results');
                                return;
                              }
                            }
                            advance();
                          }}
                          sx={{
                            borderRadius: 99,
                            px: 5,
                            fontWeight: 800,
                            background: `linear-gradient(135deg, ${GOLD}, #FF8F00)`,
                            color: '#1A1200',
                            boxShadow: `0 0 30px ${GOLD}44`,
                            '&:hover': { background: 'linear-gradient(135deg, #FFC233, #FF9D1C)' },
                          }}
                        >
                          {phase === 'redraw-revealed' && redrawOrigin === 'results'
                            ? 'تأیید و بازگشت به نتایج'
                            : reelRank === 1
                              ? 'تأیید و نمایش سکوی برنده‌ها'
                              : 'تأیید و جایگاه بعدی'}
                        </Button>
                      </Stack>
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>

              {(phase === 'spinning' || phase === 'redraw-wait') && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  {raffle.options?.showParticipants === true
                    ? `در حال انتخاب از میان ${toPersianDigits(String(raffle.draw?.participants ?? 0))} شرکت‌کننده…`
                    : 'در حال انتخاب برندهٔ خوش‌شانس…'}
                </Typography>
              )}
            </Stack>
          )}

          {/* ------------------------------------------------ نتایج: سکو + لیست + قرعه‌کشی مجدد */}
          {phase === 'results' && (
            <Box sx={{ width: '100%', maxWidth: 940, maxHeight: '100%', overflowY: 'auto', py: 1 }}>
              <Stack spacing={3.5} alignItems="center">
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <EmojiEventsIcon sx={{ fontSize: 38, color: GOLD, filter: `drop-shadow(0 0 14px ${GOLD}66)` }} />
                    <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 32, sm: 42 }, lineHeight: 1.2 }}>
                      برندگان قرعه‌کشی
                    </Typography>
                  </Stack>
                </motion.div>

                {/* سکو */}
                <Stack
                  direction="row"
                  spacing={{ xs: 1, sm: 2.5 }}
                  alignItems="flex-end"
                  justifyContent="center"
                  sx={{ width: '100%' }}
                >
                  {podiumOrder.map((w, i) =>
                    w ? (
                      <motion.div
                        key={`${w.rank}-${w.userId}`}
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + i * 0.2, type: 'spring', stiffness: 180, damping: 18 }}
                        style={{ flex: 1, maxWidth: 250 }}
                      >
                        <Box sx={glass({ borderRadius: 4, p: 2, textAlign: 'center' })}>
                          <Stack spacing={0.75} alignItems="center">
                            <EmojiEventsIcon
                              sx={{
                                fontSize: w.rank === 1 ? 46 : 34,
                                color: RANK_COLORS[w.rank],
                                filter: `drop-shadow(0 0 14px ${RANK_COLORS[w.rank]}66)`,
                              }}
                            />
                            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: w.rank === 1 ? 26 : 20, lineHeight: 1.2 }}>
                              {w.name}
                            </Typography>
                            {w.phoneNumber && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.4)', direction: 'ltr', letterSpacing: 1 }}
                              >
                                {toPersianDigits(w.phoneNumber)}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                              {w.prizeTitle}
                            </Typography>
                            {formatAmount(w.prizeAmount) && (
                              <Typography variant="body2" fontWeight={800} sx={{ color: RANK_COLORS[w.rank] }}>
                                {formatAmount(w.prizeAmount)}
                              </Typography>
                            )}
                            {onRedrawRank && raffle.status === 'drawn' && (
                              <Button
                                size="small"
                                startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
                                onClick={() => setRedrawConfirm(w)}
                                sx={{
                                  color: 'rgba(255,255,255,0.45)',
                                  fontSize: 12,
                                  '&:hover': { color: '#4DD0E1', bgcolor: 'rgba(77,208,225,0.08)' },
                                }}
                              >
                                قرعه‌کشی مجدد
                              </Button>
                            )}
                            <Box
                              sx={{
                                width: '100%',
                                height: podiumHeights[i],
                                borderRadius: '12px 12px 6px 6px',
                                background: `linear-gradient(180deg, ${RANK_COLORS[w.rank]}D0, ${RANK_COLORS[w.rank]}22)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.4)`,
                              }}
                            >
                              <Typography sx={{ fontSize: 52, fontFamily: DISPLAY_FONT, color: BG, lineHeight: 1 }}>
                                {toPersianDigits(String(w.rank))}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </motion.div>
                    ) : (
                      <Box key={`empty-${i}`} sx={{ flex: 1, maxWidth: 250 }} />
                    ),
                  )}
                </Stack>

                {/* سایر برنده‌ها */}
                {others.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} style={{ width: '100%' }}>
                    <Box sx={glass({ borderRadius: 4, p: 2.5 })}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.55)' }}>
                        سایر برنده‌ها
                      </Typography>
                      <Stack spacing={1}>
                        {others.map((w) => (
                          <Stack
                            key={`${w.rank}-${w.userId}`}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              p: 1,
                              borderRadius: 2.5,
                              border: '1px solid rgba(255,255,255,0.07)',
                              bgcolor: 'rgba(255,255,255,0.03)',
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              {toPersianDigits(String(w.rank))}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700}>
                                {w.name}
                              </Typography>
                              {w.phoneNumber && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.35)', direction: 'ltr', letterSpacing: 1 }}
                                >
                                  {toPersianDigits(w.phoneNumber)}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                              {w.prizeTitle}
                              {formatAmount(w.prizeAmount) ? ` — ${formatAmount(w.prizeAmount)}` : ''}
                            </Typography>
                            {onRedrawRank && raffle.status === 'drawn' && (
                              <IconButton
                                size="small"
                                onClick={() => setRedrawConfirm(w)}
                                sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#4DD0E1' } }}
                              >
                                <ReplayIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  </motion.div>
                )}

                {/* اثبات سلامت */}
                {raffle.draw?.seedHash && (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ maxWidth: '100%', px: 2 }}>
                    <VerifiedIcon sx={{ fontSize: 15, color: '#22C55E', flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.35)', wordBreak: 'break-all', direction: 'ltr', textAlign: 'left' }}
                    >
                      {`provably-fair · ${raffle.draw.algorithm} · seed-hash: ${raffle.draw.seedHash}`}
                    </Typography>
                  </Stack>
                )}

                <Button
                  onClick={onClose}
                  sx={{
                    borderRadius: 99,
                    px: 5,
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                  }}
                >
                  بستن
                </Button>
              </Stack>
            </Box>
          )}
        </Box>

        {/* تایید قرعه‌کشی مجدد */}
        <Dialog
          open={!!redrawConfirm}
          onClose={() => !redrawBusy && setRedrawConfirm(null)}
          PaperProps={{ sx: glass({ bgcolor: 'rgba(17,24,44,0.9)', color: '#fff', p: 1, maxWidth: 420 }) }}
        >
          {redrawConfirm && (
            <Box sx={{ p: 2.5 }} dir="rtl">
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReplayIcon sx={{ color: '#4DD0E1' }} />
                  <Typography variant="h6" fontWeight={800}>
                    قرعه‌کشی مجدد {rankLabel(redrawConfirm.rank)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  «{redrawConfirm.name}» از برنده‌ها حذف می‌شود و جایگزین از ادامهٔ همان ترتیب قطعی قرعه‌کشی انتخاب
                  می‌شود. این عمل قابل بازگشت نیست.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button color="inherit" onClick={() => setRedrawConfirm(null)} sx={{ opacity: 0.7 }}>
                    انصراف
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => beginRedraw(redrawConfirm)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 800,
                      bgcolor: '#4DD0E1',
                      color: '#06222a',
                      '&:hover': { bgcolor: '#6ADCEB' },
                    }}
                  >
                    حذف و قرعه‌کشی مجدد
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Dialog>
      </Box>
    </Dialog>
  );
};

export default RaffleDrawStage;
